import { useState, useCallback, useEffect } from "react";
import { useLocation } from "wouter";

/**
 * Custom hook for navigation guard with unsaved changes protection
 * Handles browser navigation, link clicks, and navigation after user actions
 * @param {Object} params - Navigation guard dependencies
 * @returns {Object} Navigation guard state and handlers
 */
export function useOperatingPointsNavigationGuard({ hasAnyUnsavedChanges }) {
  const [, setLocation] = useLocation();
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] =
    useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Wrapper for setLocation that checks for unsaved changes
  const navigateWithUnsavedCheck = useCallback(
    (newLocation) => {
      if (hasAnyUnsavedChanges) {
        setPendingNavigation(newLocation);
        setIsUnsavedChangesDialogOpen(true);
      } else {
        setLocation(newLocation);
      }
    },
    [hasAnyUnsavedChanges, setLocation]
  );

  // Handle browser navigation (refresh, close tab, etc.)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasAnyUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return ""; // Some browsers require a return value
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasAnyUnsavedChanges]);

  // Intercept Link clicks from sidebar and other navigation
  useEffect(() => {
    if (!hasAnyUnsavedChanges) return;

    const handleLinkClick = (e) => {
      // Check if the click is on a Link component (wouter Link)
      const linkElement = e.target.closest("a[href]");
      if (!linkElement) return;

      const href = linkElement.getAttribute("href");
      if (!href) return;

      // Don't intercept if it's the same page or an external link
      if (
        href.startsWith("http")
        || href.startsWith("mailto:")
        || href.startsWith("tel:")
      ) {
        return;
      }

      // Don't intercept if it's the current page
      if (href === window.location.pathname) {
        return;
      }

      // Intercept the navigation
      e.preventDefault();
      e.stopPropagation();

      setPendingNavigation(href);
      setIsUnsavedChangesDialogOpen(true);
    };

    // Use capture phase to catch events early
    document.addEventListener("click", handleLinkClick, true);

    return () => {
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [hasAnyUnsavedChanges]);

  // Handle navigation after user action in unsaved changes dialog
  const handleNavigationAfterAction = useCallback(() => {
    if (pendingNavigation) {
      setLocation(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, setLocation]);

  return {
    navigateWithUnsavedCheck,
    handleNavigationAfterAction,
    isUnsavedChangesDialogOpen,
    setIsUnsavedChangesDialogOpen,
    pendingNavigation,
    setPendingNavigation,
  };
}
